import { IconArrowNarrowRight } from '@tabler/icons-react';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';

import { getSmartCardsData } from 'api/getSmartCardsData';
import { AppConfig } from 'AppConfig';
import axios from 'axios';

import SmartCard from '../../../common/bstCard/smartCard';
import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { hasPermission } from 'utils/common';
import { FoMaterialTable } from '../../../common/FoMaterialTable';
import * as AppActionTypes from '../../../store/actions/appstate';
import { store } from '../../../store/store';
import * as ReportActions from '../../frontoffice/actions/actions';
import HideSmartCardDialog from './HideSmartCardDialog';
import SmartCardsLoading from 'features/frontoffice/component/smartCardsLoading';
import { sortReports } from 'utils/reportSort';
import NoAvailableReports from 'features/frontoffice/component/noAvailableReports';
import { BREAKPOINT } from 'common/Constant';
import { setSmartCardsReportsSessionDefaults } from './smartCardNavigation';
import { applySmartCardVdsOverlay } from 'features/frontoffice/smartCardVdsOverlay';
import { buildMetricsFilters, getFilterHash } from 'features/metrics/metricsActions';

// In-memory cache with TTL to avoid refetching
const smartCardsCache = { data: null, ts: 0, tenantCd: null };
const CACHE_TTL_MS = 60_000;

const sortByNeedsAttention = (list) => sortReports(list, 'needs_attention', { smart: true });

const SeeAllSmartCardsLink = ({ onClick, sx }) => (
  <Link
    component={RouterLink}
    to="/bi/reports"
    onClick={onClick}
    underline="none"
    sx={{
      fontSize: '14px',
      fontStyle: 'normal',
      fontFamily: 'Noto Sans',
      fontWeight: 600,
      color: '#2F54A5',
      display: 'inline-flex',
      alignItems: 'center',
      lineHeight: '150%',
      letterSpacing: '0%',
      gap: 0.5,
      '&:hover': { color: '#068DF1', textDecoration: 'none', background: 'transparent' },
      whiteSpace: 'nowrap',
      ...sx,
    }}
  >
    See All Smart Cards
    <IconArrowNarrowRight size={16} />
  </Link>
);

const SmartCardOpportunities = React.memo(forwardRef((_, ref) => {
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());
  const [smartCardsData, setSmartCardsData] = useState(() => []); // lazy init
  const [isLoading, setIsLoading] = useState(true); // Loading state for skeleton
  const [chartRecoveryModal, setChartRecoveryModal] = useState(false);
  const [recoverableCharts, setRecoverableCharts] = useState([]);
  const [selectedRecoverableCharts, setSelectedRecoverableCharts] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const permissions = useSelector((state) => state.user.permissions);
  const canViewSmartCards = hasPermission(AppConfig.PERMISSION.FO_SMART_CARD_VIEW, permissions);
  const reduxSmarts = useSelector((state) => state.fo.smartsBase); 
  const vdsByScId = useSelector((state) => state.fo.smartCardVdsByScId); // sc-vds display values
  const vdsLoadingFlag = useSelector((state) => state.fo.smartCardVdsLoading);
  const vdsSettledFilterHash = useSelector((state) => state.fo.smartCardVdsFilterHash);
  const appliedFilters = useSelector((state) => state.globalFilters?.applied);
  const defaultsSeeded = useSelector((state) => state.globalFilters?.defaultsSeeded);
  const navigate = useNavigate();
  const [selectedSmartCardId, setSelectedSmartCardId] = useState({ id: null, title: '' });
  const [showSmartCard, setShowSmartCard] = useState(false);
  // Throttled viewport width (rAF) to reduce layout thrash
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1440);

  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setViewportWidth(window.innerWidth);
      });
    };
    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Fetch smart cards with cache and normalize
  useEffect(() => {
    if (!canViewSmartCards) {
      return;
    }

    let active = true;
    const now = Date.now();

    // Invalidate cache if tenant changed
    if (smartCardsCache.tenantCd !== currentTenantCd) {
      smartCardsCache.data = null;
      smartCardsCache.ts = 0;
      smartCardsCache.tenantCd = currentTenantCd;
    }

    if (smartCardsCache.data && now - smartCardsCache.ts < CACHE_TTL_MS) {
      if (active) {
        startTransition(() => {
        const activeCacheData = sortByNeedsAttention(smartCardsCache.data.filter(c=>c.dismissed === false));
          setSmartCardsData(activeCacheData);
          // Initialize favoriteIds from cached data
          setFavoriteIds(new Set(smartCardsCache.data.filter((c) => c.favorited).map((c) => c.id)));
        });
        setIsLoading(false);
      }
      return;
    }

    const fetchSmartCardsData = async () => {
      try {
        setIsLoading(true);
        const response = await getSmartCardsData();
        const raw = (response?.smarts || []).map((c) => ({ ...c, favorited: !!c.favorited}));
        const activeOnly = raw.filter(c=>c.dismissed === false)
        const sorted = sortByNeedsAttention(activeOnly);
        if (!active) return;
        smartCardsCache.data = sorted;
        smartCardsCache.ts = Date.now();
        smartCardsCache.tenantCd = currentTenantCd;
        startTransition(() => {
          setSmartCardsData(sorted);
          // Initialize favoriteIds from the data
          setFavoriteIds(new Set(sorted.filter((c) => c.favorited).map((c) => c.id)));
        });
        setIsLoading(false); // Hide skeleton when data is ready
      } catch (error) {
        if (active) {
          console.error('Error fetching smart cards data:', error);
          setSmartCardsData([]);
          setIsLoading(false);
        }
      }
    };
    fetchSmartCardsData();

    return () => {
      active = false;
    };
  }, [currentTenantCd, refreshTrigger, canViewSmartCards]);

  // Invalidate cache when navigating to Home to sync favorites from Smart Cards page
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = prevPathnameRef.current;

    // Only trigger if we navigated FROM Smart Cards page TO Home page
    if (previousPath.includes('/frontoffice/smartcards') && (currentPath === '/frontoffice' || currentPath === '/frontoffice/')) {
      // Invalidate cache and trigger refetch
      smartCardsCache.ts = 0;
      setRefreshTrigger((prev) => prev + 1);
    }

    // Update previous pathname
    prevPathnameRef.current = currentPath;
  }, [location.pathname]);

  // Sync with Redux state when it changes (from Smart Cards page favorite toggles)
  useEffect(() => {
    if (reduxSmarts && Array.isArray(reduxSmarts) && reduxSmarts.length > 0) {
      // Update local state if we have data from Redux
      const sorted = sortByNeedsAttention(reduxSmarts.map((c) => ({ ...c, favorited: !!c.favorited })));
      const visibleSmartCard = sorted.filter(c=>!c.dismissed);
      startTransition(() => {
        setSmartCardsData(visibleSmartCard);
        setFavoriteIds(new Set(sorted.filter((c) => c.favorited).map((c) => c.id)));
      });
      // Update cache with Redux data
      if (smartCardsCache.tenantCd === currentTenantCd) {
        smartCardsCache.data = sorted;
        smartCardsCache.ts = Date.now();
      }
    }
  }, [reduxSmarts, currentTenantCd]);

  // ---------------------------------------------------------------------------
  // Smart Card display values (pmpm, costTrend, estimatedSavings, reachImpactPct,
  // quartile) and the Needs Attention rank (rankTrend) come from /tbl/sc-vds,
  // overlaid by scId. The fo-detail fallback is controlled by
  // SC_VDS_FALLBACK_TO_FO_DETAIL in smartCardVdsOverlay.js. Re-sort after the
  // overlay because the rank only exists once sc-vds values are applied.
  // ---------------------------------------------------------------------------
  const overlaidCards = useMemo(
    () => sortByNeedsAttention(applySmartCardVdsOverlay(smartCardsData, vdsByScId)),
    [smartCardsData, vdsByScId]
  );

  // Mirror the metric cards: while /tbl/sc-vds is loading for the applied
  // filter context, temporarily hide the smart cards behind the skeleton
  // instead of showing stale values.
  const appliedFilterHash = useMemo(
    () => getFilterHash(buildMetricsFilters(appliedFilters)),
    [appliedFilters]
  );
  const isKpiLoading = canViewSmartCards && (
    !defaultsSeeded ||
    vdsLoadingFlag ||
    appliedFilterHash !== vdsSettledFilterHash
  );
  // Expand to 4 columns on large desktops and tablets
  const cardLimit = viewportWidth >= BREAKPOINT.largeDesktop || 
    (viewportWidth >= BREAKPOINT.tablet && viewportWidth < BREAKPOINT.smallDesktop) ? 4 : 3;

  const limitedCards = useMemo(() => overlaidCards.slice(0, cardLimit), [overlaidCards, cardLimit]);
  const placeholderCards = useMemo(() => Array.from({ length: cardLimit }, (_, index) => ({id: `placeholder-${index}`, isPlaceholder: true})), [cardLimit]);
  const showPlaceholders = isLoading && limitedCards?.length === 0;
  const displayCards = showPlaceholders ? placeholderCards : limitedCards ?? [];

  const handleHideSmartCard = () => {
    if (selectedSmartCardId?.id) {
          handleDismiss(selectedSmartCardId?.id);
          setShowSmartCard(false);
          setSelectedSmartCardId({ id: null, title: '' });
    }
  };

  const handleDismiss = useCallback((id) => {
    // Optimistically remove from UI
    startTransition(() => {
      setSmartCardsData((prev) => sortByNeedsAttention(prev.filter((c) => c.id !== id)));
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    });

    // Update cache
    if (smartCardsCache.data) {
      smartCardsCache.data = smartCardsCache.data.filter((c) => c.id !== id);
    }

    // Persist to backend
    store.dispatch(ReportActions.dismissReport(id));
  }, []);

  const showSmartRecoverableCharts = useCallback(() => {
    axios
      .post(AppConfig.FO_INITIAL_URL + '/init/getmuted', {
        category: AppConfig.REPORT_CATEGORY.SMART.code
      })
      .then((res) => {
        setChartRecoveryModal(true);
        setRecoverableCharts(res.data);
      })
      .catch((err) => {
        store.dispatch({
          type: AppActionTypes.APPSTATE_SET_APP_ERROR,
          payload: err
        });
      });
  }, []);

  const recoverCharts = useCallback(() => {
    axios
      .post(AppConfig.FO_INITIAL_URL + `/init/recovercharts`, selectedRecoverableCharts)
      .then((res) => {
        // Clear cache to force refetch
        smartCardsCache.data = null;
        smartCardsCache.ts = 0;

        // Close modal
        setChartRecoveryModal(false);
        setRecoverableCharts([]);
        setSelectedRecoverableCharts([]);

        // Refetch data without page reload
        setIsLoading(true);
        getSmartCardsData()
          .then((response) => {
            const raw = (response?.smarts || []).map((c) => ({ ...c, favorited: !!c.favorited }));
            const activeOnly = raw.filter(c=>c.dismissed === false)
            const sorted = sortByNeedsAttention(activeOnly);
            smartCardsCache.data = sorted;
            smartCardsCache.ts = Date.now();
            smartCardsCache.tenantCd = currentTenantCd;
            startTransition(() => {
              setSmartCardsData(sorted);
              // Reinitialize favoriteIds after recovery
              setFavoriteIds(new Set(sorted.filter((c) => c.favorited).map((c) => c.id)));
            });
            setIsLoading(false);
          })
          .catch((err) => {
            console.error('Error refetching smart cards:', err);
            setIsLoading(false);
          });
      })
      .catch((err) => {
        console.error('Error recovering charts:', err);
      });
  }, [selectedRecoverableCharts, currentTenantCd]);

  const onRecoveryCancel = useCallback(() => {
    setSelectedRecoverableCharts([]);
    setChartRecoveryModal(false);
  }, []);

  const handleSeeAllClick = useCallback((e) => {
    e.preventDefault();
    setSmartCardsReportsSessionDefaults();
    navigate('/bi/reports');
  }, [navigate]);

  if (!canViewSmartCards) {
    return null;
  }

  return (
    <>
      <Box
        ref={ref}
        sx={{
          fontFamily: 'rubik',
          fontSize: {
            xs: '18px',
            sm: '18px',
            md: '20px'
          },

          fontWeight: 500,
          fontStyle: 'normal',
          lineHeight: '30px',
          color: '#121A22',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          mt: '64px',
          width: '100%',
        }}>
          <Box sx={{ flex: 1, minWidth: 0}}>
            <Box
              sx={{
                fontFamily: 'rubik',
                fontSize: 20,
                fontWeight: 600,
                fontStyle: 'normal',
                lineHeight: '30px',
                color: '#000',
                letterSpacing: '0.2px',
              }}
            >
              <span>Top Smart Card Opportunities</span>
            </Box>
            <Typography variant="body2" sx={{ color: '#121A22', mt: 0.5, fontSize: '14px', lineHeight: '21px', fontFamily: 'Noto Sans'}}>
              Examines high-impact areas and transforms the data into an actionable, insights-driven view.
            </Typography>
          </Box>
          <SeeAllSmartCardsLink
            onClick={handleSeeAllClick}
            sx={{ ml: 'auto', mt: 0.5, flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}
          />
        </Box>

      <Box
        sx={{
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: '24px',
          border: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          position: 'relative',
          pb: 6,
        }}>
          {!isLoading && limitedCards.length === 0 ? (
            <NoAvailableReports isSmartCards />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                  xl: 'repeat(4, 1fr)'
                },
                gap: '24px'
              }}>
                {displayCards.map((card) => (
                    <Box
                      key={card.id}
                      sx={{
                        pt: 0,
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                      <SmartCard item={card} reportType={AppConfig.SMART_CAROUSEL} kpiLoading={isKpiLoading} setShowSmartCard={setShowSmartCard} setSelectedSmartCardId={setSelectedSmartCardId}/>
                    </Box>
                ))}
            </Box>
          )}
          {/* Mobile layout — centered below cards */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, justifyContent: 'center', pt: 3 }}>
            <SeeAllSmartCardsLink onClick={handleSeeAllClick} />
          </Box>
      </Box>

      {/* Recover Smart Cards Modal */}
      {hasPermission(AppConfig.PERMISSION.FO_SMARTCARD_RECOVER, permissions) && (
        <Modal open={chartRecoveryModal} onClose={onRecoveryCancel}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              borderRadius: '24px',
              border: '1px solid #959696',
              py: '40px',
              px: {
                xs: '16px',
                sm: '32px',
                md: '40px'
              },
              width: {
                xs: '95%',
                sm: '90%',
                md: '700px'
              },
              overflowY: 'visible',
              background: '#FFF'
            }}>
            {/* Title and Close button row */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: '500', fontFamily: 'Rubik', fontSize: '20px', color: '#121A22', lineHeight: '30px' }}>
                Recover Smart Cards
              </Typography>
              <IconButton onClick={onRecoveryCancel} size="large" aria-label="close">
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography sx={{ mb: 3, fontSize: '16px', color: '#121A22', fontFamily: 'Rubik', fontWeight: '400' }}>
              Select the smart cards you would like to recover and display again on your dashboard.
            </Typography>
            <Box sx={{ maxHeight: '350px', overflowY: 'auto' }}>
              <FoMaterialTable
                columns={[{ title: 'SMART CARD', field: 'name' }]}
                data={recoverableCharts}
                options={{
                  selection: true,
                  search: false,
                  paging: false,
                  toolbar: false
                }}
                components={{ OverlayLoading: () => <div /> }}
                onSelectionChange={(rows) => {
                  const selectedCharts = rows.map((row) => row.id);
                  setSelectedRecoverableCharts(selectedCharts);
                }}
              />
            </Box>
            <Divider sx={{ width: '100%', mt: '40px', mb: '24px' }} />
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '24px',
                flexDirection: {
                  xs: 'column-reverse',
                  sm: 'row'
                }
              }}>
              <Button
                variant="outlined"
                onClick={onRecoveryCancel}
                sx={{
                  borderRadius: '24px',
                  width: {
                    xs: '100%',
                    sm: '188px'
                  },
                  height: '48px',
                  px: 3
                }}>
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={recoverCharts}
                disabled={!selectedRecoverableCharts.length > 0}
                sx={{
                  borderRadius: '24px',
                  width: {
                    xs: '100%',
                    sm: '188px'
                  },
                  height: '48px',
                  px: 3,
                  borderColor: '#2F54A5',
                  color: '#2F54A5',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    borderColor: '#2F54A5'
                  }
                }}>
                Recover
              </Button>
            </Box>
          </Box>
        </Modal>
      )}
    {showSmartCard && (
        <HideSmartCardDialog
          open={showSmartCard}
          onClose={() => setShowSmartCard(false)}
          handleHideSmartCard={handleHideSmartCard}
          selectedSmartCardId={selectedSmartCardId}
        />
      )}
    </>
  );
}));

export default SmartCardOpportunities;
